import { forEach } from 'lodash';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { PaginationInput } from '@sifca-monorepo/ecommerce-generator';
import {
  BarcodeType,
  BarcodeFindInput,
  ProductAttributeType,
  GetProductBarcodesCacheGQL,
  GetProductAttributesCacheGQL,
  InternalProductType,
  SearchInternalProductGQL,
  InternalProductFilterInput,
  GetInternalProductWithFilterGQL,
  GetCatalogueCategoriesByLayerAndParentCacheGQL,
} from '@sifca-monorepo/ecommerce-generator';

import { ICatalogueCategoryTreeType } from './products.types';
import { PosService } from '../../pos/pos.service';
import { IPagination } from '@diktup/frontend/models';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private barcodes: BehaviorSubject<BarcodeType[] | null> = new BehaviorSubject(null);
  private product: BehaviorSubject<InternalProductType | null> = new BehaviorSubject(null);
  private pagination: BehaviorSubject<IPagination | null> = new BehaviorSubject(null);
  private products: BehaviorSubject<InternalProductType[] | null> = new BehaviorSubject(null);
  private attributes: BehaviorSubject<ProductAttributeType[] | null> = new BehaviorSubject(null);

  filter: BehaviorSubject<InternalProductFilterInput | null> = new BehaviorSubject(null);
  categories: BehaviorSubject<ICatalogueCategoryTreeType[] | null> = new BehaviorSubject(null);

  constructor(
    private posService: PosService,
    private getProductBarcodesGQL: GetProductBarcodesCacheGQL,
    private getProductAttributesGQL: GetProductAttributesCacheGQL,
    private searchInternalProductGQL: SearchInternalProductGQL,
    private getInternalProductWithFilterGQL: GetInternalProductWithFilterGQL,
    private getCatalogueCategoriesByLayerAndParentGQL: GetCatalogueCategoriesByLayerAndParentCacheGQL,
  ) {}

  get filter$(): Observable<InternalProductFilterInput> {
    return this.filter.asObservable();
  }

  get categories$(): Observable<ICatalogueCategoryTreeType[]> {
    return this.categories.asObservable();
  }

  get pagination$(): Observable<IPagination> {
    return this.pagination.asObservable();
  }

  get product$(): Observable<InternalProductType> {
    return this.product.asObservable();
  }

  get products$(): Observable<InternalProductType[]> {
    return this.products.asObservable();
  }

  get attributes$(): Observable<ProductAttributeType[]> {
    return this.attributes.asObservable();
  }

  get internalProductsPagination$(): Observable<InternalProductType[]> {
    return this.products.asObservable();
  }

  get barcodes$(): Observable<BarcodeType[]> {
    return this.barcodes.asObservable();
  }

  insertNodeIntoTree(category: ICatalogueCategoryTreeType, id: string, newCategories: ICatalogueCategoryTreeType[]) {
    if (category.id === id) {
      category.selected = true;
      if (newCategories) {
        category.children = newCategories;
      }
    } else if (category?.children?.length > 0) {
      for (let i = 0; i < category?.children?.length; i++) {
        this.insertNodeIntoTree(category.children[i], id, newCategories);
      }
    }
  }

  getCategories(layer: number, parent?: string): Observable<ICatalogueCategoryTreeType[]> {
    return this.posService.pos$.pipe(
      take(1),
      switchMap((pos) =>
        this.getCatalogueCategoriesByLayerAndParentGQL.fetch({ target: { pos: pos.id }, layer, ...(parent && { parent }) }).pipe(
          tap((response: any) => {
            if (response.data) {
              const categories = this.categories.value;
              if (!categories?.length) {
                this.categories.next(response.data.getCatalogueCategoriesByLayerAndParentCache);
                return response.data.getCatalogueCategoriesByLayerAndParentCache;
              } else {
                forEach(categories, (category, index) => {
                  this.insertNodeIntoTree(category, parent, response.data.getCatalogueCategoriesByLayerAndParentCache);
                  if (index === categories.length - 1) {
                    this.categories.next(categories);
                    return categories;
                  }
                  return [];
                });
              }
            }
          }),
        ),
      ),
    );
  }

  getProducts(pagination?: PaginationInput): Observable<InternalProductType[]> {
    return this.posService.pos$.pipe(
      take(1),
      switchMap((pos) =>
        this.getInternalProductWithFilterGQL
          .fetch({ pagination, sort: [{ createdAt: -1 }], filter: { ...this.filter.value, target: { pos: [pos.id] } } })
          .pipe(
            tap((response: any) => {
              this.products.next(response.data.getInternalProductWithFilter.objects);
              this.pagination.next({
                length: response.data.getInternalProductWithFilter.count,
                size: pagination?.limit ? pagination.limit : 10,
                page: pagination?.page ? pagination.page : 0,
              });
              return this.products.value;
            }),
          ),
      ),
    );
  }

  searchInternalProduct(searchString: string, pagination = { page: 0, limit: 10 }): Observable<InternalProductType[]> {
    return this.searchInternalProductGQL.fetch({ searchString, pagination }).pipe(
      map((response) => {
        this.products.next(response.data.searchInternalProduct.objects as InternalProductType[]);
        this.pagination.next({
          page: pagination?.page ? pagination.page : 0,
          size: pagination?.limit ? pagination.limit : 10,
          length: response.data.searchInternalProduct.count,
        });
        return this.products.value;
      }),
    );
  }

  getProductAttributes(): Observable<ProductAttributeType[]> {
    return this.getProductAttributesGQL.fetch().pipe(
      tap((response: any) => {
        this.attributes.next(response.data.getProductAttributesCache);
        return response.data.getProductAttributesCache;
      }),
    );
  }

  getByProductAndInternalProductAndAttributes(input: any): Observable<BarcodeType[]> {
    return null;
    // return this.getByProductAndInternalProductAndAttributesGQL.fetch({ input }).pipe(
    //   tap((response: any) => {
    //     this.barcodes.next([response.data.getByProductAndInternalProductAndAttributes, ...this.barcodes.value]);
    //     return response.data.getByProductAndInternalProductAndAttributes;
    //   }),
    // );
  }

  getProductBarcodes(productId: string): Observable<BarcodeType[]> {
    return this.getProductBarcodesGQL.fetch({ productId }).pipe(
      tap((response: any) => {
        if (response.data) {
          this.barcodes.next(response.data.getProductBarcodesCache);
          return response.data.getProductBarcodesCache;
        }
        return [];
      }),
    );
  }

  getByBarcode(barcode: string): Observable<boolean> {
    return null;
    // return this.getByBarcodeGQL.fetch({ barcode }).pipe(
    //   map((response: any) => {
    //     if (response.data) {
    //       return response?.data?.getByBarcode?.id ? true : false;
    //     }
    //   }),
    // );
  }

  getByProductAndAttributes(input: BarcodeFindInput): Observable<boolean> {
    return null;
    // return this.getByProductAndAttributesGQL.fetch({ input }).pipe(
    //   map((response: any) => {
    //     if (response.data) {
    //       return response?.data?.getByProductAndAttributes?.id ? true : false;
    //     }
    //   }),
    // );
  }

  getProductById(id: string): Observable<InternalProductType> {
    return this.products.pipe(
      take(1),
      map((products) => {
        const product = products.find((item) => item.id === id) || null;
        this.product.next(product);
        return product;
      }),
      switchMap((product) => {
        if (!product) {
          throwError(() => new Error('Could not find product with id of ' + id + '!'));
        }
        return of(product);
      }),
    );
  }
}
